import { useMemo, useRef, useState } from 'react';
import { t } from '@lingui/macro';
import { useLocation, useNavigate } from 'react-router-dom';
import uniqBy from 'lodash/uniqBy';
import isEmpty from 'lodash/isEmpty';

import BarChart from '@decisiv/iconix/lib/components/BarChart';
import Grid from '@decisiv/iconix/lib/components/Grid';
import ChessCastle from '@decisiv/iconix/lib/components/ChessCastle';
import Network from '@decisiv/iconix/lib/components/Network';
import Truck from '@decisiv/iconix/lib/components/Truck';

import Flex from '@decisiv/ui-components/lib/components/Flex';
import Loading from '@decisiv/ui-components/lib/components/Loading';

import OptionsList from '@decisiv/ui-components/lib/atoms/OptionsList';
import Popover, {
  InstanceRef,
} from '@decisiv/ui-components/lib/components/Popover';

import { useCurrentUserContext } from 'utils/User';
import ROUTE_NAMES from 'setup/Routing/routeNames';
import useSentinelTeam from 'hooks/useSentinelTeam';
import useFeatureFlag, { FEATURE_FLAGS } from 'utils/useFeatureFlag';

import useUserMenuInfoQuery from './useUserMenuInfoQuery';

import { Button } from './styles';

export default function AppsLinks() {
  const [active, setActive] = useState(false);
  const popoverRef = useRef<InstanceRef>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const selectedId = location.pathname.startsWith(ROUTE_NAMES.admin.management)
    ? 'management'
    : 'fleetItem';

  const { isSuperUser, isOemManager, isAdmin, isFleetManager } =
    useCurrentUserContext();
  const { belongsToOemSegment } = useSentinelTeam();
  const { data, loading } = useUserMenuInfoQuery();

  const pd1188FFEnabled = useFeatureFlag(
    FEATURE_FLAGS.PD_1188_ROUTE_BASED_FLEET_MANAGEMENT,
  );

  const portalLinks = useMemo(
    () => data?.currentUserProfile?.user?.portalUrls || [],
    [data?.currentUserProfile?.user?.portalUrls],
  );

  const portalLinksItems = useMemo(
    () =>
      uniqBy(
        portalLinks.map(
          (portalLink: { id: string; productName: string; url: string }) => {
            const productName = portalLink.productName.toUpperCase();

            return {
              id: portalLink.id,
              label: t`${productName} PORTAL`,
              onSelect: () => {
                window.open(portalLink.url, '_blank', 'noopener,noreferrer');
              },
              decoration: {
                type: 'Badge',
                icon: ChessCastle,
                color: 'success',
                palette: 'pastel',
              },
            };
          },
        ),
        'label',
      ),
    [portalLinks],
  );

  const insiteItem = useMemo(
    () => ({
      id: 'insite',
      label: t`Insite`,
      onSelect: () => {
        window.open(
          data?.currentUserProfile?.user?.insiteUrl,
          '_blank',
          'noopener,noreferrer',
        );
      },
      decoration: {
        type: 'Badge',
        icon: BarChart,
        color: 'carrotCake',
        palette: 'pastel',
      },
    }),
    [data?.currentUserProfile?.user?.insiteUrl],
  );

  const managementItem = useMemo(
    () => ({
      id: 'management',
      label: t`Fleet Management`,
      onSelect: () => {
        navigate(ROUTE_NAMES.admin.management);
      },
      decoration: {
        type: 'Badge',
        icon: Network,
        color: 'information',
        palette: 'pastel',
      },
    }),
    [navigate],
  );

  const fleetItem = useMemo(
    () => ({
      id: 'fleetItem',
      label: t`PSSM Fleet`,
      onSelect: () => {
        navigate(ROUTE_NAMES.service_events);
      },
      decoration: {
        type: 'Badge',
        icon: Truck,
        color: 'warning',
        palette: 'pastel',
      },
    }),
    [navigate],
  );

  const items = useMemo(() => {
    const baseItems =
      pd1188FFEnabled &&
      (isSuperUser || isOemManager || (isFleetManager && isAdmin))
        ? [fleetItem, managementItem]
        : [];

    let visibleItems = [...baseItems];

    if (data?.currentUserProfile?.user?.insiteUrl)
      visibleItems = [...visibleItems, insiteItem];

    if (isSuperUser || !belongsToOemSegment)
      visibleItems = [...visibleItems, ...portalLinksItems];

    return visibleItems;
  }, [
    pd1188FFEnabled,
    isSuperUser,
    isOemManager,
    managementItem,
    fleetItem,
    portalLinksItems,
    data?.currentUserProfile?.user?.insiteUrl,
    insiteItem,
    belongsToOemSegment,
    isAdmin,
    isFleetManager,
  ]);

  const handleToggleActive = () => {
    setActive((oldState) => !oldState);
  };

  if (!loading && isEmpty(items)) return null;

  const navigateItem = (item: string) => {
    popoverRef.current?.hide();
    items.find((i) => i.id === item)?.onSelect?.();
  };

  return (
    <Popover
      ref={popoverRef}
      zIndex={10}
      onShow={handleToggleActive}
      onHide={handleToggleActive}
      target={
        <Button aria-label={t`Show Apps links`} active={active}>
          <Grid />
        </Button>
      }
      showArrow={false}
    >
      {loading ? (
        <Flex padding={2}>
          <Loading title={t`Loading...`} size="small" />
        </Flex>
      ) : (
        <OptionsList
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          items={items}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          onChange={navigateItem}
          selected={selectedId}
        />
      )}
    </Popover>
  );
}
